import { Fragment, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  GIRDI_SEKMELERI,
  GIRDI_SEKME_TURLERI,
  dosyaIcerikTuru,
  girdiDosyaKabul,
  girdiIcerikHatasi,
  girdiIslemi,
  oemSayfaHatasi,
  type DosyaBelgeBagYaniti,
  type DosyaBelgeGovdesi,
  type DosyaBelgeSatiri,
  type DosyaGirdiTuru,
  type DosyaYuklemeYaniti,
  type GirdiIslemi,
  type GirdiSekmesi,
  type GirdiTurGrubu,
  type IzBagiGovdesi,
  type KokenParcasi,
  type KokenSatiri,
  type OemSayfaGovdesi,
  type OemSayfaSatiri,
  type ProjeBelgeleriYaniti,
  type ProjeSekmesiYaniti,
  type RaporSatiri,
  type RiserGirisiYazmaYaniti,
  type RiserSecenegi,
  type RiserSekmesiYaniti,
  type SetHat,
} from "@oasis/domain";
import { api } from "@/lib/api";
import { useVeri, dosyaSec, dosyaYukle, tarih } from "@/lib/veri";
import { useOturum } from "@/lib/oturum";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { Band, Bos, Buton, Etiket, Hata, Kutu, Modal, Sayfa, etiket, girdi } from "@/components/parcalar";
import { belgeLinki } from "@/components/pdf-sayfa";
import { belgeSayaci, hatAdi, riserSuz, sayfaAdedi } from "@/lib/riser-belgeleri";
import {
  ANA_YOL,
  BASKA_RISER,
  YUKLE_YOLU,
  aletEtiketi,
  aletSecenekleri,
  belgeGorunumu,
  damgaParcalari,
  girdiYetkisi,
  gruplaraBol,
  ilkSekme,
  imzalayanCipleri,
  imzalayanEksikMi,
  inspektorSecenekleri,
  izDenetimi,
  izYolu,
  kaldirYolu,
  kapsamCipi,
  kapsamSecenekleri,
  kokenDenetimi,
  kokenHucresi,
  kokenSecenekleri,
  kokenYolu,
  oemDegistiMi,
  oemGorunumu,
  oemSayfaYolu,
  pencereDenetimi,
  raporAdi,
  raporAltMetni,
  raporCipi,
  raporSayaci,
  riserYolu,
  sayfaHucresi,
  sekmeAdi,
  turAdi,
  uygulananMetin,
  yuklemeDamgasi,
  yuklemeDugmesi,
  yuklenmediMetni,
  type OemGorunumu,
} from "@/lib/proje-belgeleri";

/**
 * D-059: Project documents. Riser dosyasının kendi adımının ekranı olmayan belgeleri ve bağları buradan girilir; girilen her şey
 * Riser Documentation'da görünür, hiçbir adımı kilitlemez. İki sekme yerel durumdur: "Project and work order" (bir kez yüklenip
 * birçok riserde görünen belgeler) ve "Riser" (riser belgeleri, köken, OEM kaydı sayfaları, rapor ekipmanı ve imzalayanı).
 * Kurallar alan paketinde, metinler ve kaydetmeden önceki denetimler lib/proje-belgeleri.ts içinde; sınıflar onaylı iki statik
 * sayfadan birebir.
 * D-063: üçüncü sekme ("NDT personnel") kalktı; NDT inspektörleri ve sertifikaları Registers › NDT inspectors'ta adlarıyla tutulur.
 * D-062: "Reports — test equipment and signer" tablosu raporun imzalayanının sonradan girildiği tek yerdir; imzalayan o kütükten
 * adıyla seçilir.
 */

/** Hap düğme (sekme, riser hapı): Riser Documentation'daki haplarla aynı sınıflar. */
const HAP = "h-kontrol-kucuk shrink-0 rounded-full border px-3 text-sm font-medium transition";
const HAP_SECILI = "border-navy bg-navy text-white dark:text-[#0a141c]";
const HAP_DIGER = "border-line bg-surface text-ink-2 hover:border-line-strong";
const TABLO_BAS_SATIRI = "border-b border-line text-left text-xs text-ink-3";
const TABLO_BASLIGI = "py-1 pr-3 font-semibold";
const TABLO_HUCRESI = "py-2 pr-3";
/** "Riser documents" satırı: project-documents--riser.html ile birebir. */
const TUR_SATIRI = "flex flex-wrap items-center gap-2 border-b border-line py-2 text-sm";
/** Küçük metin kutusu (riser arama kutusu). */
const KUCUK_GIRDI = "h-kontrol-kucuk rounded-md border border-line bg-surface px-3 text-sm";
/** "+ Add" seçimi: küçük düğmenin sınıflarıyla çizilen seçim kutusu (Fitup'taki çoklu seçim deseni; ilk seçenek düğmenin adıdır). */
const EKLE_SECIMI = "h-kontrol-kucuk w-24 rounded-md border border-line bg-surface px-2.5 text-sm font-medium text-ink transition hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-45";

/** Riser seçicinin araması iki rota arasında (riser seçili / seçili değil) korunur. */
const suzgec = { arama: "" };

const tarihBicimi = (iso: string) => tarih(iso);
const hataMetni = (e: unknown) => (e instanceof Error ? e.message : cevir("Error"));

/** İşlem deseni (Saha.tsx, kutuk-belgeleri.tsx): süren işin adı ve hata bandı; iş bitince okuma ucu yeniden çağrılır. */
function useIslem(yenile: () => Promise<unknown>) {
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const dene = async (is: string, f: () => Promise<unknown>) => {
    setHata(null);
    setMesgul(is);
    try {
      await f();
      await yenile();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(null);
    }
  };
  return { hata, setHata, mesgul, dene };
}

/** Kullanıcının bu işlemdeki yetkisi ve yetkisizse düğmenin ipucu (kural alan paketinde: GIRDI_YETKILERI). */
function useYetki(islem: GirdiIslemi) {
  const { ben } = useOturum();
  return ben ? girdiYetkisi(ben.rol, islem) : { yetkili: false, ipucu: undefined };
}

/** Yetkiye bağlı küçük düğme (`Buton islem=…` deseni): yetkisizse pasif ve ipuçlu. İşlem birden çok yetkiyle yapılabildiği için kural `useYetki`den okunur. */
function GirdiButonu({ islem, onClick, children, disabled, mesgul, title }: { islem: GirdiIslemi; onClick: () => void | Promise<void>; children: ReactNode; disabled?: boolean; mesgul?: boolean; title?: string }) {
  const y = useYetki(islem);
  return <Buton kucuk onClick={onClick} disabled={disabled || !y.yetkili} mesgul={mesgul} title={y.yetkili ? title : y.ipucu}>{children}</Buton>;
}

function KutuHatasi({ mesaj }: { mesaj: string | null }) {
  return mesaj ? <div className="mb-2"><Hata mesaj={mesaj} /></div> : null;
}

/** Dosya seçtirir: türün kabul ettiği içerikler; kimlik plakası fotoğrafında telefonda kamera (Teslim.tsx deseni). İptalde null. */
const dosyaIste = (tur: DosyaGirdiTuru) => dosyaSec(girdiDosyaKabul(tur), tur === "kimlik_plakasi" ? "environment" : undefined);

/** Dosyanın içeriğini başlık baytlarından denetler (sunucuyla aynı kural) ve yükler. Bağ yazmaz; dönen kimlik bağ gövdesine konur. */
async function dosyaGonder(tur: DosyaGirdiTuru, dosya: File): Promise<DosyaYuklemeYaniti> {
  const hata = girdiIcerikHatasi(tur, dosyaIcerikTuru(new Uint8Array(await dosya.slice(0, 8).arrayBuffer())));
  if (hata) throw new Error(cevir(hata));
  return dosyaYukle<DosyaYuklemeYaniti>(YUKLE_YOLU, dosya, { tur });
}

const bagEkle = (govde: DosyaBelgeGovdesi) => api.post<DosyaBelgeBagYaniti>(ANA_YOL, govde);

/** Kaldırma onayı: bağ kaldırılır, PDF ve kimin kaldırdığı kaydı durur. */
const kaldirmaOnayi = () => window.confirm(cevir("Remove this document? The PDF and the record of who removed it are kept."));

/** Belge adı: belge kimliği geldiyse bağlantı (sayfa aralığı varsa o sayfadan açılır), gelmediyse düz metin. */
function BelgeAdi({ s }: { s: DosyaBelgeSatiri }) {
  const g = belgeGorunumu(s);
  if (!g.dokumanId) return <span>{g.metin}</span>;
  return <a href={belgeLinki(g.dokumanId, g.sayfa)} target="_blank" rel="noreferrer" className="font-semibold underline" title={cevir("Open document")}>{g.metin}</a>;
}

export function ProjeBelgeleri({ rn }: { rn?: string }) {
  const [sekme, setSekme] = useState<GirdiSekmesi>(ilkSekme(rn));
  const { veri, hata, yenile } = useVeri<ProjeBelgeleriYaniti>(ANA_YOL);
  return (
    <Sayfa
      ust={cevir("5.7 · Riser file")}
      baslik={cevir("Project documents")}
      alt={<>{cevir("Documents and links of the riser file that have no screen of their own. Everything entered here shows up on")} <b>{cevir("Riser Documentation")}</b>{cevir(". Nothing here blocks a step.")}</>}
    >
      <Kutu padded={false}>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          {GIRDI_SEKMELERI.map((s) => (
            <button key={s} type="button" aria-pressed={sekme === s} onClick={() => setSekme(s)} className={cn(HAP, sekme === s ? HAP_SECILI : HAP_DIGER)}>
              {sekmeAdi(s)}
            </button>
          ))}
        </div>
      </Kutu>
      {hata ? (
        <Hata mesaj={hata} />
      ) : !veri ? (
        <Bos>{cevir("Loading...")}</Bos>
      ) : sekme === "proje" ? (
        <ProjeSekmesi v={veri.proje} yenile={yenile} />
      ) : (
        <RiserSekmesi rn={rn} riserler={veri.riserler} />
      )}
    </Sayfa>
  );
}

/* ---------------------------------------------------------------- sekme 1: Project and work order ---------------------------------------------------------------- */

/** Bir kez yüklenip birçok riserde görünen belgeler: tablo, "+ Add document" penceresi ve sayfa notu. */
function ProjeSekmesi({ v, yenile }: { v: ProjeSekmesiYaniti; yenile: () => Promise<void> }) {
  const { hata, mesgul, dene } = useIslem(yenile);
  const [pencere, setPencere] = useState(false);
  const kaldir = (s: DosyaBelgeSatiri) => {
    if (kaldirmaOnayi()) void dene(`kaldir:${s.id}`, () => api.post<DosyaBelgeBagYaniti>(kaldirYolu(s.id)));
  };
  return (
    <>
      <Kutu baslik={cevir("Documents for all risers or one work order")} aksiyon={belgeSayaci(v.belgeler.length)}>
        <KutuHatasi mesaj={hata} />
        <p className="mb-2 text-sm text-ink-2">{cevir("Uploaded once, shown on every riser they apply to.")}</p>
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className={TABLO_BAS_SATIRI}>
                <th className={TABLO_BASLIGI}>{cevir("Type")}</th>
                <th className={TABLO_BASLIGI}>{cevir("Applies to")}</th>
                <th className={TABLO_BASLIGI}>{cevir("Document")}</th>
                <th className={TABLO_BASLIGI}>{cevir("Pages")}</th>
                <th className={TABLO_BASLIGI}>{cevir("Uploaded")}</th>
                <th className="py-1 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {v.belgeler.map((s, i) => {
                const c = kapsamCipi(s);
                return (
                  <tr key={s.id} className={i < v.belgeler.length - 1 ? "border-b border-line" : undefined}>
                    <td className={TABLO_HUCRESI}><b>{turAdi(s.tur)}</b></td>
                    <td className={TABLO_HUCRESI}><Etiket tone="info">{c.cip}</Etiket><span className="ml-1 text-xs text-ink-3">{c.ek}</span></td>
                    <td className={TABLO_HUCRESI}>
                      <BelgeAdi s={s} />
                      {s.not && <span className="block text-xs text-ink-3">{s.not}</span>}
                    </td>
                    <td className={TABLO_HUCRESI}>{sayfaHucresi(s)}</td>
                    <td className={TABLO_HUCRESI}><span className="text-xs text-ink-3">{yuklemeDamgasi(s, tarihBicimi)}</span></td>
                    <td className="py-2">
                      <GirdiButonu islem={girdiIslemi(s.tur)} title={cevir("Remove")} disabled={mesgul !== null} mesgul={mesgul === `kaldir:${s.id}`} onClick={() => kaldir(s)}>✕</GirdiButonu>
                    </td>
                  </tr>
                );
              })}
              {!v.belgeler.length && <tr><td colSpan={6} className="py-2 text-ink-3">{cevir("No document.")}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <GirdiButonu islem="belge" disabled={mesgul !== null} onClick={() => setPencere(true)}>{cevir("+ Add document")}</GirdiButonu>
        </div>
      </Kutu>
      <Band tip="bilgi">{cevir("Work order numbers are not typed: they are read from the inspection reports of the risers. A customer purchase order can be attached once per work order. Removing a document keeps the PDF and the record of who removed it.")}</Band>
      {pencere && (
        <EklePenceresi
          v={v}
          onKapat={() => setPencere(false)}
          onKaydedildi={async () => {
            setPencere(false);
            await yenile();
          }}
        />
      )}
    </>
  );
}

/**
 * "+ Add document" penceresi: Type, Applies to (bilinen iş emirleri ya da bütün riserler) ve kaç risere uygulandığı, PDF, Pages, Note.
 * "Upload PDF" dosyayı hemen yükler (bağ yazmaz; sayfa sayısı öğrenilir); Save önce saf denetimi çalıştırır, sonra bağı yazar.
 */
function EklePenceresi({ v, onKapat, onKaydedildi }: { v: ProjeSekmesiYaniti; onKapat: () => void; onKaydedildi: () => Promise<void> }) {
  const turler: readonly DosyaGirdiTuru[] = GIRDI_SEKME_TURLERI.proje;
  const [tur, setTur] = useState<DosyaGirdiTuru>(turler[0]!);
  const [deger, setDeger] = useState<string | null>(null);
  const [dosya, setDosya] = useState<DosyaYuklemeYaniti | null>(null);
  const [sayfalar, setSayfalar] = useState("");
  const [not, setNot] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState<"yukle" | "kaydet" | null>(null);
  const secenekler = kapsamSecenekleri(tur, v);
  const secenek = secenekler.find((s) => s.deger === deger) ?? secenekler[0] ?? null;
  const uygulanan = secenek ? uygulananMetin(secenek.riserSayisi) : null;
  const kilit = mesgul !== null;

  const yukle = async () => {
    const f = await dosyaIste(tur);
    if (!f) return;
    setHata(null);
    setMesgul("yukle");
    try {
      setDosya(await dosyaGonder(tur, f));
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(null);
    }
  };

  const kaydet = async () => {
    const d = pencereDenetimi({ tur, secenek, dosya, sayfalar, not }, { isEmirleri: v.isEmirleri.map((e) => e.no), etkin: v.belgeler });
    if (!d.govde) {
      setHata(cevir(d.hata ?? "Error"));
      return;
    }
    setHata(null);
    setMesgul("kaydet");
    try {
      await bagEkle(d.govde);
      await onKaydedildi();
    } catch (e) {
      setHata(hataMetni(e));
      setMesgul(null);
    }
  };

  return (
    <Modal baslik={cevir("Add document")} onKapat={onKapat} genis kilitli={kilit}>
      <KutuHatasi mesaj={hata} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={etiket} htmlFor="belge-ekle-tur">{cevir("Type")}</label>
          <select id="belge-ekle-tur" className={girdi} value={tur} disabled={kilit} onChange={(e) => setTur(e.target.value as DosyaGirdiTuru)}>
            {turler.map((t) => <option key={t} value={t}>{turAdi(t)}</option>)}
          </select>
        </div>
        <div>
          <label className={etiket} htmlFor="belge-ekle-kapsam">{cevir("Applies to")}</label>
          <select id="belge-ekle-kapsam" className={girdi} value={secenek?.deger ?? ""} disabled={kilit} onChange={(e) => setDeger(e.target.value)}>
            {secenekler.map((s) => <option key={s.deger} value={s.deger}>{s.etiket}</option>)}
          </select>
          {uygulanan && <span className={cn("mt-1 block text-xs", uygulanan.uyari ? "text-warn" : "text-ink-3")}>{uygulanan.metin}</span>}
        </div>
        <div>
          <label className={etiket}>PDF</label>
          <div className="flex flex-wrap items-center gap-2">
            <Buton kucuk disabled={kilit} mesgul={mesgul === "yukle"} onClick={yukle}>{mesgul === "yukle" ? cevir("Uploading...") : dosya ? cevir("Change file") : cevir("Upload PDF")}</Buton>
            {dosya ? (
              <>
                <a href={belgeLinki(dosya.id)} target="_blank" rel="noreferrer" className="text-sm font-semibold underline" title={cevir("Open document")}>{dosya.ad}</a>
                {dosya.toplam > 0 && <span className="text-xs text-ink-3">{sayfaAdedi(dosya.toplam)}</span>}
              </>
            ) : (
              <span className="text-sm text-ink-3">{cevir("not selected")}</span>
            )}
          </div>
        </div>
        <div>
          <label className={etiket} htmlFor="belge-ekle-sayfalar">{cevir("Pages (optional)")}</label>
          <input id="belge-ekle-sayfalar" className={girdi} type="text" placeholder={cevir("all pages, or 1-6")} value={sayfalar} disabled={kilit} onChange={(e) => setSayfalar(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className={etiket} htmlFor="belge-ekle-not">{cevir("Note (optional)")}</label>
          <input id="belge-ekle-not" className={girdi} type="text" value={not} disabled={kilit} onChange={(e) => setNot(e.target.value)} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <GirdiButonu islem="belge" disabled={kilit} mesgul={mesgul === "kaydet"} onClick={kaydet}>{cevir("Save")}</GirdiButonu>
        <Buton kucuk disabled={kilit} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- sekme 2: Riser ---------------------------------------------------------------- */

function RiserSekmesi({ rn, riserler }: { rn?: string; riserler: RiserSecenegi[] }) {
  return (
    <>
      <RiserSecici rn={rn} riserler={riserler} />
      {rn ? <RiserGirisi key={rn} rn={rn} /> : <Bos>{cevir("Select a riser to see its documents.")}</Bos>}
    </>
  );
}

/** Riser picker: arama kutusu (RN ve Assembly SN), riser hapları (RN sırasıyla; belge sayısı yazılmaz), sağda seçili riserin Batch ve Work order çipleri. */
function RiserSecici({ rn, riserler }: { rn?: string; riserler: RiserSecenegi[] }) {
  const [arama, setArama] = useState(suzgec.arama);
  const liste = riserSuz(riserler, arama, null);
  const secili = riserler.find((r) => r.rn === rn);
  return (
    <Kutu padded={false}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <input
          className={KUCUK_GIRDI}
          placeholder={cevir("Search riser no. or Assembly SN...")}
          aria-label={cevir("Search riser no. or Assembly SN...")}
          value={arama}
          onChange={(e) => {
            suzgec.arama = e.target.value;
            setArama(e.target.value);
          }}
        />
        <span className="flex flex-wrap gap-1">
          {liste.map((r) => (
            <Link key={r.rn} to="/project-documents/$rn" params={{ rn: r.rn }} aria-current={r.rn === rn ? "page" : undefined} className={cn(HAP, "inline-flex items-center", r.rn === rn ? HAP_SECILI : HAP_DIGER)}>
              {r.rn}
            </Link>
          ))}
          {!liste.length && <span className="text-sm text-ink-3">{cevir("No riser in this filter.")}</span>}
        </span>
        {secili && (
          <span className="ml-auto flex flex-wrap items-center gap-1">
            {secili.batchNo && <Etiket>{`${cevir("Batch")} ${secili.batchNo}`}</Etiket>}
            {secili.isEmri && <Etiket>{`${cevir("Work order")} ${secili.isEmri}`}</Etiket>}
          </span>
        )}
      </div>
    </Kutu>
  );
}

/** Seçili riserin girişleri. `key={rn}` ile kurulur: riser değişince önceki riserin verisi ekranda kalmaz. */
function RiserGirisi({ rn }: { rn: string }) {
  const { veri, hata, yenile } = useVeri<RiserSekmesiYaniti>(riserYolu(rn));
  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  return (
    <>
      <RiserBelgeKutusu rn={rn} gruplar={veri.belgeler} yenile={yenile} />
      <KokenKutusu rn={rn} kokenler={veri.kokenler} riserSecenekleri={veri.riserSecenekleri} yenile={yenile} />
      <OemKutusu rn={rn} oem={veri.oem} yenile={yenile} />
      <RaporKutusu rn={rn} v={veri} yenile={yenile} />
      <Band tip="bilgi">{cevir("“Riser documents”, “Lines and parts from another riser” and “OEM record pages” are entered by the Quality Lead. Identification plate photos and “Reports — test equipment and signer” can also be entered by the Field Inspector. Reports are uploaded on their own screens (Finishing, Component); the equipment is added here; the signer can be selected at upload and added or changed here.")}</Band>
    </>
  );
}

/**
 * "Riser documents": beş tür, her biri bir satır. Belge yoksa "Not uploaded." ve yükleme düğmesi; tekil türde (CoC, DNV Survey Report)
 * belge varken yükleme düğmesi yerine kaldırma; çoklu türde her belgenin kaldırma düğmesi ve ekleme düğmesi.
 */
function RiserBelgeKutusu({ rn, gruplar, yenile }: { rn: string; gruplar: GirdiTurGrubu[]; yenile: () => Promise<void> }) {
  const { hata, mesgul, dene } = useIslem(yenile);
  const yukle = async (tur: DosyaGirdiTuru) => {
    const f = await dosyaIste(tur);
    if (!f) return;
    await dene(`yukle:${tur}`, async () => {
      const d = await dosyaGonder(tur, f);
      await bagEkle({ kapsam: "riser", anahtar: rn, tur, dokumanId: d.id });
    });
  };
  const kaldir = (s: DosyaBelgeSatiri) => {
    if (kaldirmaOnayi()) void dene(`kaldir:${s.id}`, () => api.post<DosyaBelgeBagYaniti>(kaldirYolu(s.id)));
  };
  return (
    <Kutu baslik={cevir("Riser documents")} aksiyon={cevir("for {0} only", rn)}>
      <KutuHatasi mesaj={hata} />
      {gruplar.map((g) => {
        const dugme = yuklemeDugmesi(g);
        const islem = girdiIslemi(g.tur);
        return (
          <div key={g.tur} className={TUR_SATIRI}>
            <span className="min-w-0 flex-1">
              <b>{turAdi(g.tur)}</b>
              <span className="block text-xs text-ink-3">
                {g.satirlar.length
                  ? damgaParcalari(g.satirlar, tarihBicimi).map((x, i) => (
                      <Fragment key={x.satir.id}>
                        {i > 0 && " · "}
                        <BelgeAdi s={x.satir} />
                        {x.damga && ` · ${x.damga}`}
                      </Fragment>
                    ))
                  : yuklenmediMetni(g.tur)}
              </span>
            </span>
            {g.satirlar.map((s) => (
              <GirdiButonu key={s.id} islem={islem} title={`${cevir("Remove")}: ${belgeGorunumu(s).metin}`} disabled={mesgul !== null} mesgul={mesgul === `kaldir:${s.id}`} onClick={() => kaldir(s)}>✕</GirdiButonu>
            ))}
            {dugme && (
              <GirdiButonu islem={islem} disabled={mesgul !== null} mesgul={mesgul === `yukle:${g.tur}`} onClick={() => yukle(g.tur)}>
                {mesgul === `yukle:${g.tur}` ? cevir("Uploading...") : dugme}
              </GirdiButonu>
            )}
          </div>
        );
      })}
    </Kutu>
  );
}

/**
 * "Lines and parts from another riser": beş hat; hattın tamamı ya da box, pipe, pin başka riserden geldiyse o riser seçilir. Seçim
 * değişince kaydedilir (ayrı Save yok). "another riser…" seçilince R### girişi açılır (Enter ya da odaktan çıkınca kaydeder, Esc
 * vazgeçer). Hattın tamamı seçiliyken parçaları "From riser {0}" çipidir.
 */
function KokenKutusu({ rn, kokenler, riserSecenekleri, yenile }: { rn: string; kokenler: KokenSatiri[]; riserSecenekleri: string[]; yenile: () => Promise<void> }) {
  const { hata, setHata, mesgul, dene } = useIslem(yenile);
  const yetki = useYetki("koken");
  const [elle, setElle] = useState<{ hat: SetHat; parca: KokenParcasi; metin: string } | null>(null);
  /** Esc ile kapanan girişin odak kaybı kaydetmesin. */
  const vazgecildi = useRef(false);

  const kaydet = (hat: SetHat, parca: KokenParcasi, ham: string) => {
    const d = kokenDenetimi(rn, hat, parca, ham);
    if (!d.govde) {
      setHata(cevir(d.hata ?? "Error"));
      return;
    }
    const govde = d.govde;
    setElle(null);
    void dene(`${hat}:${parca}`, () => api.post<RiserGirisiYazmaYaniti>(kokenYolu(rn), govde));
  };

  const hucre = (s: KokenSatiri, parca: KokenParcasi, baslik: string) => {
    const h = kokenHucresi(s, parca);
    if (h.tip === "cip") return <Etiket tone="warn">{cevir("From riser {0}", h.rn)}</Etiket>;
    const ad = `${hatAdi(s.hat)} · ${baslik}`;
    if (elle && elle.hat === s.hat && elle.parca === parca) {
      return (
        <input
          className={girdi}
          type="text"
          autoFocus
          placeholder="R###"
          aria-label={`${ad} · ${cevir("another riser…")}`}
          value={elle.metin}
          onChange={(e) => setElle({ ...elle, metin: e.target.value })}
          onBlur={() => {
            if (vazgecildi.current) return;
            if (elle.metin.trim()) kaydet(s.hat, parca, elle.metin);
            else setElle(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              vazgecildi.current = true;
              setElle(null);
            }
          }}
        />
      );
    }
    return (
      <select
        className={girdi}
        aria-label={ad}
        value={h.secili ?? ""}
        disabled={!yetki.yetkili || mesgul !== null}
        title={yetki.ipucu}
        onChange={(e) => {
          if (e.target.value !== BASKA_RISER) return kaydet(s.hat, parca, e.target.value);
          vazgecildi.current = false;
          setHata(null);
          setElle({ hat: s.hat, parca, metin: "" });
        }}
      >
        {kokenSecenekleri(rn, riserSecenekleri, h.secili).map((o) => <option key={o.deger} value={o.deger}>{o.etiket}</option>)}
      </select>
    );
  };

  const kolonlar = [cevir("Whole line"), cevir("Box"), cevir("Pipe"), cevir("Pin")];
  return (
    <Kutu baslik={cevir("Lines and parts from another riser")} aksiyon={cevir("saved as soon as a choice is made")}>
      <KutuHatasi mesaj={hata} />
      <p className="mb-2 text-sm text-ink-2">{cevir("Leave “this riser” unless the line or the part was taken from another riser. With a whole line chosen, its box, pipe and pin follow it.")}</p>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className={TABLO_BAS_SATIRI}>
              <th className={TABLO_BASLIGI}>{cevir("Line")}</th>
              <th className={TABLO_BASLIGI}>{kolonlar[0]}</th>
              <th className={TABLO_BASLIGI}>{kolonlar[1]}</th>
              <th className={TABLO_BASLIGI}>{kolonlar[2]}</th>
              <th className="py-1 font-semibold">{kolonlar[3]}</th>
            </tr>
          </thead>
          <tbody>
            {kokenler.map((s, i) => (
              <tr key={s.hat} className={i < kokenler.length - 1 ? "border-b border-line" : undefined}>
                <td className={TABLO_HUCRESI}><b>{hatAdi(s.hat)}</b></td>
                <td className={TABLO_HUCRESI}>{hucre(s, "hat", kolonlar[0]!)}</td>
                <td className={TABLO_HUCRESI}>{hucre(s, "box", kolonlar[1]!)}</td>
                <td className={TABLO_HUCRESI}>{hucre(s, "pipe", kolonlar[2]!)}</td>
                <td className="py-2">{hucre(s, "pin", kolonlar[3]!)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Kutu>
  );
}

/**
 * "OEM record pages": OEM kitabında beş hattın ve iki DNV belgesinin sayfa aralığı. Değer kitabın iç dizininden hesaplanır; düzeltme
 * kutusu odaktan çıkınca kaydeder, kutu boşaltılınca dizine dönülür. Hattı başka riserden gelen satırda değer vericinin kitabındandır
 * ve kutu pasiftir.
 */
function OemKutusu({ rn, oem, yenile }: { rn: string; oem: RiserSekmesiYaniti["oem"]; yenile: () => Promise<void> }) {
  const { hata, dene } = useIslem(yenile);
  const yetki = useYetki("oemSayfa");
  const kaydet = (s: OemSayfaSatiri, sayfalar: string) => {
    const govde: OemSayfaGovdesi = { yer: s.yer, sayfalar };
    void dene(s.yer, () => api.post<RiserGirisiYazmaYaniti>(oemSayfaYolu(rn), govde));
  };
  /** Pasif kutunun ipucu: hattın geldiği riser, kitabın PDF'i yok ya da yetki. */
  const ipucu = (s: OemSayfaSatiri, g: OemGorunumu) => {
    if (g.pasif === "koken") return cevir(oemSayfaHatasi(s.yer, s.kokenRn) ?? "") || undefined;
    if (g.pasif === "kitap_yok") return cevir("This riser has no OEM record book PDF; pages cannot be corrected.");
    return yetki.ipucu;
  };
  return (
    <Kutu baslik={cevir("OEM record pages")} aksiyon={cevir("pages of the OEM record book (MRB)")}>
      <KutuHatasi mesaj={hata} />
      <p className="mb-2 text-sm text-ink-2">{cevir("Read from the document index of the OEM record book. Type a page range only where the index is missing or wrong; clear the box to go back to the index.")}</p>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className={TABLO_BAS_SATIRI}>
              <th className={TABLO_BASLIGI}>{cevir("Part of the book")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Pages")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Correction")}</th>
              <th className="py-1 font-semibold">{cevir("Source")}</th>
            </tr>
          </thead>
          <tbody>
            {oem.satirlar.map((s, i) => {
              const g = oemGorunumu(s, oem.kitap);
              // Kayıtlı değer değişince satır baştan kurulur: kutu sunucudaki değeri gösterir.
              return <OemSatiri key={`${s.yer}:${g.duzeltme}`} s={s} g={g} son={i === oem.satirlar.length - 1} kilit={g.pasif !== null || !yetki.yetkili} ipucu={ipucu(s, g)} onKaydet={(sayfalar) => kaydet(s, sayfalar)} />;
            })}
          </tbody>
        </table>
      </div>
    </Kutu>
  );
}

function OemSatiri({ s, g, son, kilit, ipucu, onKaydet }: { s: OemSayfaSatiri; g: OemGorunumu; son: boolean; kilit: boolean; ipucu: string | undefined; onKaydet: (sayfalar: string) => void }) {
  const [metin, setMetin] = useState(g.duzeltme);
  return (
    <tr className={son ? undefined : "border-b border-line"}>
      <td className={TABLO_HUCRESI}><b>{g.ad}</b></td>
      <td className={TABLO_HUCRESI}>{g.sayfa}</td>
      <td className={TABLO_HUCRESI}>
        <input
          className={girdi}
          type="text"
          placeholder={g.ornek}
          aria-label={`${cevir("Correction")}: ${g.ad}`}
          value={metin}
          disabled={kilit}
          title={ipucu}
          onChange={(e) => setMetin(e.target.value)}
          onBlur={() => {
            if (oemDegistiMi(metin, s.elle)) onKaydet(metin.trim());
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </td>
      <td className="py-2"><Etiket tone={g.kaynak.ton}>{g.kaynak.metin}</Etiket></td>
    </tr>
  );
}

/**
 * "Reports — test equipment and signer": riserin yüklü basınç testi sertifikaları ve NDT raporları. Sertifikada üstünde adı yazan test
 * ekipmanı (Registers › Instruments), NDT raporunda raporu imzalayan NDT inspektörü (Registers › NDT inspectors): seçili çipler ve
 * "+ Add". Rapor burada yüklenmez; kendi ekranında yüklenir.
 * D-062: imzalayan kütükteki inspektörlerden adıyla seçilir (serbest giriş ve "new code…" kalktı); imzalayanı boş NDT raporunda sarı
 * "signer missing" görünür. Yüklemede seçilmeyen imzalayan sonradan buradan girilir; verilen liste öncekinin yerine geçer.
 */
function RaporKutusu({ rn, v, yenile }: { rn: string; v: RiserSekmesiYaniti; yenile: () => Promise<void> }) {
  const { hata, setHata, mesgul, dene } = useIslem(yenile);
  const yetki = useYetki("iz");
  const kilit = !yetki.yetkili || mesgul !== null;

  /** Saf denetimden geçerse yazar; denetim hatası kutunun üstünde görünür. Değişiklik yoksa istek atılmaz. */
  const yaz = (r: RaporSatiri, govde: IzBagiGovdesi) => {
    const d = izDenetimi(r, govde, v.aletler, v.inspektorler);
    if (d.hata) {
      setHata(cevir(d.hata));
      return;
    }
    const yeni = d.govde;
    if (yeni) void dene(`iz:${r.dokumanId}`, () => api.post<RiserGirisiYazmaYaniti>(izYolu(r.dokumanId), yeni));
  };

  /** Seçili çipler: `deger` bağ gövdesine giden değerdir (alet kodu, inspektör kimliği), ekrana yalnız `ad` yazılır. */
  const cipler = (secili: readonly { deger: string; ad: string }[], kaldir: (deger: string) => void) =>
    secili.map(({ deger, ad }) => (
      <Etiket key={deger} tone="info">
        {ad}
        <button type="button" className="disabled:cursor-not-allowed disabled:opacity-45" title={yetki.yetkili ? cevir("Remove") : yetki.ipucu} aria-label={`${cevir("Remove")}: ${ad}`} disabled={kilit} onClick={() => kaldir(deger)}>✕</button>
      </Etiket>
    ));

  const ekipman = (r: RaporSatiri) => {
    const secenekler = aletSecenekleri(v.aletler, r.aletler);
    return (
      <span className="flex flex-wrap items-center gap-1">
        {cipler(r.aletler.map((kod) => ({ deger: kod, ad: aletEtiketi(kod, v.aletler) })), (kod) => yaz(r, { aletler: r.aletler.filter((x) => x !== kod) }))}
        <select
          className={EKLE_SECIMI}
          aria-label={`${cevir("+ Add")}: ${cevir("Test equipment")}`}
          value=""
          disabled={kilit || !secenekler.length}
          title={yetki.ipucu}
          onChange={(e) => {
            if (e.target.value) yaz(r, { aletler: [...r.aletler, e.target.value] });
          }}
        >
          <option value="">{cevir("+ Add")}</option>
          {gruplaraBol(secenekler).map((g) =>
            g.grup ? (
              <optgroup key={g.grup} label={g.grup}>
                {g.secenekler.map((o) => <option key={o.deger} value={o.deger}>{o.etiket}</option>)}
              </optgroup>
            ) : (
              g.secenekler.map((o) => <option key={o.deger} value={o.deger}>{o.etiket}</option>)
            ),
          )}
        </select>
      </span>
    );
  };

  /**
   * D-062: çipler ve seçenekler kütükteki inspektörlerin adlarıdır (seçili pasif inspektör " · inactive" ekiyle; seçeneklerde yalnız
   * aktif ve seçili olmayanlar, ad sırasıyla). İmzalayanı olmayan raporda sarı "signer missing". Kimlik ekrana yazılmaz.
   */
  const imzalayan = (r: RaporSatiri) => {
    const secenekler = inspektorSecenekleri(v.inspektorler, r.imzalayanlar);
    return (
      <span className="flex flex-wrap items-center gap-1">
        {imzalayanEksikMi(r, v.inspektorler) ? (
          <Etiket tone="warn">{cevir("signer missing")}</Etiket>
        ) : (
          cipler(imzalayanCipleri(r.imzalayanlar, v.inspektorler).map((c) => ({ deger: c.id, ad: c.etiket })), (id) => yaz(r, { ndtInspektor: r.imzalayanlar.filter((x) => x !== id) }))
        )}
        <select
          className={EKLE_SECIMI}
          aria-label={`${cevir("+ Add")}: ${cevir("Signed by")}`}
          value=""
          disabled={kilit || !secenekler.length}
          title={yetki.ipucu}
          onChange={(e) => {
            if (e.target.value) yaz(r, { ndtInspektor: [...r.imzalayanlar, e.target.value] });
          }}
        >
          <option value="">{cevir("+ Add")}</option>
          {secenekler.map((o) => <option key={o.deger} value={o.deger}>{o.etiket}</option>)}
        </select>
      </span>
    );
  };

  return (
    <Kutu baslik={cevir("Reports — test equipment and signer")} aksiyon={raporSayaci(v.raporlar.length, rn)}>
      <KutuHatasi mesaj={hata} />
      <p className="mb-2 text-sm text-ink-2">{cevir("For each report on file: the test equipment named on it (from Registers › Instruments) and the NDT inspector who signed it (from Registers › NDT inspectors). Their calibration certificates and NDT certificates then appear on Riser Documentation.")}</p>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className={TABLO_BAS_SATIRI}>
              <th className={TABLO_BASLIGI}>{cevir("Report")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Test equipment")}</th>
              <th className="py-1 font-semibold">{cevir("Signed by")}</th>
            </tr>
          </thead>
          <tbody>
            {v.raporlar.map((r, i) => {
              const alt = raporAltMetni(r);
              return (
                <tr key={`${r.tip}:${r.dokumanId}`} className={i < v.raporlar.length - 1 ? "border-b border-line" : undefined}>
                  <td className={TABLO_HUCRESI}>
                    <b>{raporAdi(r)}</b> <Etiket>{raporCipi(r)}</Etiket>
                    <span className="block text-xs text-ink-3">
                      {alt.once.length > 0 && `${alt.once.join(" · ")} · `}
                      <a href={belgeLinki(r.dokumanId, r.satir.sayfa)} target="_blank" rel="noreferrer" className="font-semibold underline" title={cevir("Open document")}>{alt.dosyaAd}</a>
                      {alt.sonra.length > 0 && ` · ${alt.sonra.join(" · ")}`}
                    </span>
                  </td>
                  <td className={TABLO_HUCRESI}>{r.tip === "basinc_testi" ? ekipman(r) : "—"}</td>
                  <td className="py-2">{r.tip === "ndt" ? imzalayan(r) : "—"}</td>
                </tr>
              );
            })}
            {!v.raporlar.length && <tr><td colSpan={3} className="py-2 text-ink-3">{cevir("No document.")}</td></tr>}
          </tbody>
        </table>
      </div>
    </Kutu>
  );
}
